import browser from '@/lib/browser'
import urls from '@/lib/urls'
import pubsub from '@/lib/pubsub'

import { hasValidUserSession } from '@/services/identity-client'

/**
 * Class responsible for dealing with page actions
 */
export default class Page {
  /**
   * @constructor
   */
  constructor() {
    this.requireLoginOn = [
      '/fish/map',
      '/purchase.html',
      '/update-payment.html',
      '/redeem.html',
      '/redeem-success.html',
    ]

    this.redirectToMapOn = [`/fish/login`]

    this.checkPurchaseRedirectOn = [
      '/account-purchase.html',
      '/fish/create-account',
      '/fish/login',
    ]

    this.checkRedeemRedirectOn = [
      '/account-purchase.html',
      '/login-purchase.html',
    ]

    this.pageParams = {
      fullProductString: urls.getFullProductParamString(),
      product: urls.getProductParam(),
      afterLogin: urls.getAfterLoginParam(),
      promo: urls.getPromoParam(),
      newTrial: urls.getTrialParam(),
      app: urls.getAppParam(),
      eliteProDeal: urls.getEliteProDealParam(),
      mode: urls.getModeParam(),
    }

    pubsub.subscribe('loginRequestCompleted', this.check.bind(this))
  }

  /**
   * Check that we are on the page that we should be
   * based on currently logged in status.
   *
   * @future - Move redirect handling into Vue Router - https://router.vuejs.org/guide/advanced/navigation-guards.html
   */
  async check(sessionChecker = hasValidUserSession) {
    const currentSession = await sessionChecker()

    const path = browser.getLocation().pathname
    let redirectPath = ''
    let excludeRedirectFromHistory = false

    // currently logged in
    if (currentSession) {
      // are we on a page that should redirect to the map
      this.redirectToMapOn.forEach((page) => {
        if (path.includes(page)) {
          redirectPath = `/fish/map`
        }
      })

      // are we on a page that should redirect if purchase params are present
      this.checkPurchaseRedirectOn.forEach((page) => {
        if (path.includes(page)) {
          // product param is present
          if (this.pageParams.fullProductString) {
            redirectPath = '/purchase.html'
            excludeRedirectFromHistory = true
          }
          // product param is not present
          else {
            redirectPath = `/fish/map`
          }
        }
      })

      // are we on a page that should redirect if redeem param is present
      this.checkRedeemRedirectOn.forEach((page) => {
        if (path.includes(page) && this.pageParams.redemption) {
          redirectPath = '/redeem.html'
        }
      })
    }
    // not logged in
    else {
      // are we on a page that requires login
      this.requireLoginOn.forEach((page) => {
        if (path.includes(page)) {
          // redirect back to the appropriate login page
          redirectPath = `/fish/login?after_login` + encodeURIComponent(page)
        }
      })
    }

    if (redirectPath) {
      // parse the share ID if available
      const shareID = urls.getShareID()
      if (shareID) {
        urls.addParam('share_id', shareID)
        urls.removeParam('_branch_match_id')
      }

      // Pass along any parameters
      const params = urls.getFullParamString()
      if (params) {
        redirectPath += '?' + params
      }

      browser.go(redirectPath, excludeRedirectFromHistory)
    }
  }

  /**
   * Entry point for the index.html page, also '/'. Looks for
   * password reset or routes to map/login depending on logged
   * in state.
   *
   * @future - Move redirect handling into Vue Router. Confirm if this is even
   * used anywhere anymore.
   */
  async index(sessionChecker = hasValidUserSession) {
    if (browser.getLocation().hash.includes('reset')) {
      const resetParam = urls.getFullParamString()
      browser.go(`/fish/login?` + resetParam + '#reset')
    } else {
      // determine if we should go to the map or to login
      let redirectUrl = `/fish/login`

      // logged in
      if (await sessionChecker()) {
        redirectUrl = `/fish/map`
      }

      // pass along any params
      const paramString = urls.getFullParamString()
      if (paramString) {
        redirectUrl += '?' + paramString
      }

      browser.go(redirectUrl)
    }
  }

  /**
   * Determine what page to go to following a login
   *
   * @future - Move redirect handling into Vue Router
   */
  redirectAfterLogin() {
    // route to the correct app
    const destinationApp = 'fish'
    let destination = `/${destinationApp}/map?${urls.getFullParamString()}`
    let redirected = false

    if (this.pageParams.redemption) {
      destination = `/fish/redeem/`
      redirected = true
    } else if (this.pageParams.fullProductString) {
      destination = '/purchase.html?' + this.pageParams.fullProductString
      redirected = true

      if (this.pageParams.promo) {
        destination += '&promo=' + this.pageParams.promo
      }

      if (this.pageParams.discount) {
        destination += '&discount=' + this.pageParams.discount
      }
    } else if (this.pageParams.newTrial) {
      destination += '?request_trial=true'
      redirected = true
    } else if (this.pageParams.eliteProDeal) {
      destination += `?pro_deal=${this.pageParams.eliteProDeal}`
      redirected = true
    }

    const shareID = urls.getShareID()
    if (shareID) {
      redirected = true
      if (destination.indexOf('?') > -1) {
        destination += '&share_id=' + shareID
      } else {
        destination += '?share_id=' + shareID
      }
    }

    const { hash } = window.location
    if (hash) {
      destination += hash
    }

    if (!redirected && this.pageParams.afterLogin) {
      try {
        const afterLoginUrl = new URL(
          this.pageParams.afterLogin,
          window.location.origin
        )
        const originalParams = new URLSearchParams(browser.getLocation().search)

        // Add any original params to the afterLogin URL
        for (const [key, value] of originalParams.entries()) {
          if (key !== 'after_login' && !afterLoginUrl.searchParams.has(key)) {
            afterLoginUrl.searchParams.append(key, value)
          }
        }

        browser.go(afterLoginUrl.toString())
      } catch (e) {
        browser.go(this.pageParams.afterLogin)
      }
    } else {
      browser.go(destination)
    }
  }
}
